import type { useSessionRunsContext } from "../../../../contexts/sessionRunsContext";

/** What one cell renders about a failure standing against it. */
export type CellErrorState = {
  hasError: boolean;
  isTimeout: boolean;
  errorMessage?: string;
  /**
   * The run settled rather than failing wholesale, and `columnErrors` names this
   * column. Held apart from a run failure so a contained failure does not read as
   * a failed sample. One run can name several columns, so this says nothing about
   * whether the row's other columns generated.
   */
  isColumnFailure?: boolean;
  /**
   * A prior verdict is still on screen beneath this column failure, so the cell
   * reads as a failed regeneration over an earlier answer rather than an absent
   * result. Drives copy only; the amber column-failure styling is unchanged.
   */
  priorResultVisible?: boolean;
  /**
   * Replaces the sentence the tooltip would otherwise lead with, for a state
   * whose cause the generic run/column leads do not describe. Absent on every
   * other cell kind, which keeps their copy exactly as it is.
   */
  errorLead?: string;
};

const NO_CELL_ERROR: CellErrorState = { hasError: false, isTimeout: false };

const runSettledAt = (updatedAt: string | Date) =>
  new Date(updatedAt).getTime();

/**
 * Whether a failure still stands against one cell, and what it should say.
 *
 * Two channels reach a cell, and it can render only one:
 *
 * - A run failure — the run itself ended `failed` or `timed_out`, so nothing on
 *   the row generated. Superseded two ways: by a later successful run covering
 *   the same cell, and by the cell's own result having come from the run that
 *   failed.
 * - A column failure — `columnErrors` names this column on a run that otherwise
 *   settled. A column's failure does not fail its run, which is why the carrying
 *   run is typically `succeeded`; reading this channel off run state alone is
 *   what leaves the message unreachable. It stands until a later covering run
 *   settles without naming the column.
 *
 * A run failure wins when one run carries both: the run's own outcome is the
 * larger fact, and the column entry still serves as its reason.
 *
 * A *contained* column failure — a run that settled while naming this column —
 * is reported only where the caller opts in through `containedColumnFailure`.
 * The channel is not kind-aware, so every kind routed through here would
 * otherwise start surfacing an alert on data that already exists, and the three
 * kinds that predate this resolver are required to behave exactly as they did.
 * They opt out and keep run-failure reporting alone.
 *
 * The opt-in carries `priorResultVisible` with it rather than beside it, so a
 * caller cannot report the failure while leaving the question of a visible
 * result unanswered: a contained failure over a verdict still on screen is a
 * failed regeneration, and calling it an absent result contradicts the cell.
 * Pairing them in one required argument is what makes the compiler name a new
 * call site that has not decided.
 */
export function resolveCellError(params: {
  sourceRowId: string;
  columnId: string;
  resultRunId: string | null | undefined;
  isLoading: boolean;
  errorRuns: ReturnType<typeof useSessionRunsContext>["sessionRuns"];
  successfulRuns: ReturnType<typeof useSessionRunsContext>["sessionRuns"];
  /** The V2 executor reports timeouts differently, so its copy is suppressed. */
  suppressTimeoutCopy: boolean;
  containedColumnFailure:
    | { report: false }
    | { report: true; priorResultVisible: boolean };
}): CellErrorState {
  const {
    sourceRowId,
    columnId,
    resultRunId,
    isLoading,
    errorRuns,
    successfulRuns,
    suppressTimeoutCopy,
    containedColumnFailure,
  } = params;

  if (isLoading) return NO_CELL_ERROR;

  const coversCell = (
    run: ReturnType<typeof useSessionRunsContext>["sessionRuns"][number],
  ) =>
    run.sourceRowId === sourceRowId &&
    (run.targetColumnIds === null || run.targetColumnIds.includes(columnId));

  const standingFailure = errorRuns.find((errorRun) => {
    if (!coversCell(errorRun)) return false;
    const failedAt = runSettledAt(errorRun.updatedAt);
    return !successfulRuns.some(
      (successRun) =>
        coversCell(successRun) && runSettledAt(successRun.updatedAt) > failedAt,
    );
  });

  if (standingFailure) {
    if (resultRunId === standingFailure.id) return NO_CELL_ERROR;
    return {
      hasError: true,
      isTimeout: !suppressTimeoutCopy && standingFailure.state === "timed_out",
      // Only the per-column entry is customer-facing. The run-level
      // `failureReason` is deliberately NOT surfaced here: it carries internal
      // ops detail (e.g. the reaper's "worker … presumed dead; reaped"), which
      // an auditor should not see. That detail stays in logs / Forge; a proper
      // customer-safe run-failure message is a post-beta follow-up.
      errorMessage: standingFailure.columnErrors?.[columnId] ?? undefined,
    };
  }

  if (!containedColumnFailure.report) return NO_CELL_ERROR;

  // Only the newest entry is read, so this scans rather than sorts: the work
  // runs for every reporting cell on every render, where sorting would allocate
  // two intermediate arrays to answer with one element. Ties keep the first
  // entry found, and the failed runs are scanned first, so a failure and a
  // settled run stamped alike resolve to the failure.
  //
  // The first qualifying entry is taken before any comparison, so a stamp that
  // does not parse cannot make a reported failure disappear: every comparison
  // against it is false, and without an incumbent the cell would fall through to
  // showing nothing at all.
  let latestColumnFailure:
    | { run: (typeof errorRuns)[number]; message: string }
    | undefined;
  let latestSettledAt = -Infinity;

  for (const runs of [errorRuns, successfulRuns]) {
    for (const run of runs) {
      const message = run.columnErrors?.[columnId];
      if (!message || !coversCell(run)) continue;
      const settledAt = runSettledAt(run.updatedAt);
      if (!latestColumnFailure || settledAt > latestSettledAt) {
        latestSettledAt = settledAt;
        latestColumnFailure = { run, message };
      }
    }
  }

  if (!latestColumnFailure) return NO_CELL_ERROR;

  const failedAt = latestSettledAt;
  const laterRunProducedIt = successfulRuns.some(
    (successRun) =>
      coversCell(successRun) &&
      !successRun.columnErrors?.[columnId] &&
      runSettledAt(successRun.updatedAt) > failedAt,
  );
  if (laterRunProducedIt) return NO_CELL_ERROR;

  return {
    hasError: true,
    isTimeout: false,
    errorMessage: latestColumnFailure.message,
    isColumnFailure: true,
    priorResultVisible: containedColumnFailure.priorResultVisible,
  };
}
